/**
 * @fileoverview ErrorState — error placeholder with optional heading + Retry button
 * @package @puffin-core/page-shell
 *
 * Renders the canonical `.error-state` family of classes from
 * @puffin-core/shared-styles/css/utilities/feedback.css.
 *
 * `role="alert"` so screen readers announce the error.
 *
 * Optional `title` renders a heading (`.error-state-title`) above the message —
 * for callers that want a short human summary ("Failed to load billing events")
 * distinct from the technical detail in `message`. (SUBDOMAIN-RESOLVE-001
 * follow-up: previously an unknown `title` prop was silently dropped by React,
 * so three port-web mailbox-holder pages that passed `title` rendered nothing;
 * `title` is now first-class.)
 *
 * When `retrying` is true, the Retry button is disabled, its label swaps
 * to `retryingLabel`, and `aria-busy="true"` is set on the surrounding
 * container — standard idempotency UX that prevents the double-tap
 * "fire the retry twice" failure mode on mobile.
 */

import PropTypes from 'prop-types';

import { useShellTranslation } from '../hooks/useShellTranslation.js';

// AAS-I18N-002 F-3: `resolveMessage` is a plain module function, so the fallback
// arrives as a resolved string PARAMETER rather than a module-level constant that
// would freeze English at load (miss-class 1).

// Phase 5 (Item E, 2026-05-08): module-level icon glyph extracted out
// of JSX so the ESLint rule's JSXText check passes. Visual identity
// stays the same — `aria-hidden="true"` on the wrapper means screen
// readers don't announce the glyph regardless.
const ERROR_ICON_GLYPH = '⚠';

/**
 * @param {*} message - Raw message, Error, error-shaped object, or empty.
 * @param {string} fallback - Localized fallback used for every empty/opaque shape.
 * @returns {React.ReactNode} the fallback, an error's message, or the node as given
 */
function resolveMessage(message, fallback) {
  if (message === null || message === undefined || message === '') return fallback;
  if (message instanceof Error) return message.message || fallback;
  if (typeof message === 'object') {
    if (typeof message.message === 'string' && message.message) return message.message;
    return fallback;
  }
  return message;
}

/**
 * @typedef {object} ErrorStateProps
 * @property {React.ReactNode} [title]
 * @property {React.ReactNode|Error|{message?: string}} [message] - a node, an Error, or an
 *   error-shaped object; empty or opaque → the translated fallback
 * @property {() => void} [onRetry] - present → a Retry button
 * @property {React.ReactNode} [retryLabel]
 * @property {boolean} [retrying=false]
 * @property {React.ReactNode} [retryingLabel]
 * @property {React.ReactNode} [actions] - further recovery actions after Retry
 */

/**
 * Every prop is optional: with no message it says the translated fallback.
 *
 * @param {ErrorStateProps} props
 * @returns {React.JSX.Element}
 */
export function ErrorState({
  title,
  message,
  onRetry,
  retryLabel,
  retrying = false,
  retryingLabel,
  actions,
}) {
  const t = useShellTranslation();
  const resolved = resolveMessage(
    message,
    t('pageState.errorFallback', { defaultValue: 'An error occurred. Please try again.' }),
  );
  /** @type {React.HTMLAttributes<HTMLDivElement>} */
  const containerProps = retrying ? { 'aria-busy': 'true' } : {};

  return (
    <div className="error-state" role="alert" data-testid="error-state" {...containerProps}>
      <div className="error-state-icon" data-testid="error-state-icon" aria-hidden="true">
        {ERROR_ICON_GLYPH}
      </div>
      {title ? (
        <h2 className="error-state-title" data-testid="error-state-title">
          {title}
        </h2>
      ) : null}
      <p className="error-state-message" data-testid="error-state-message">
        {resolved}
      </p>
      {onRetry || actions ? (
        <div className="error-state-actions">
          {onRetry ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onRetry}
              disabled={retrying}
              aria-disabled={retrying}
              data-testid="error-state-retry"
            >
              {retrying
                ? (retryingLabel ?? t('common.retrying', { defaultValue: 'Retrying…' }))
                : (retryLabel ?? t('common.retry', { defaultValue: 'Retry' }))}
            </button>
          ) : null}
          {actions}
        </div>
      ) : null}
    </div>
  );
}

ErrorState.propTypes = {
  title: PropTypes.node,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.instanceOf(Error)]),
  onRetry: PropTypes.func,
  retryLabel: PropTypes.node,
  retrying: PropTypes.bool,
  retryingLabel: PropTypes.node,
  /**
   * Further recovery actions rendered after Retry in the same `.error-state-actions` row — e.g.
   * `<AppErrorBoundary>`'s Go-home link (F-TOCONTAIN-3 U2b-2).
   */
  actions: PropTypes.node,
};
